import { Stack, Typography } from '@mui/material'
import { addDays, format, isFuture, isSameDay, isToday, Locale, startOfWeek } from 'date-fns'
import { FC, useState } from 'react'
import { CalendarData } from '../../../../types/calendar'
import { CreateEventMenu } from './CreateEventMenu'
import { EventItem } from './EventItem'

const daysToRender = Array.from({ length: 7 }).map((_, index) => index)

type Props = {
    currentMonth: Date
    currentWeek: number
    selectedDate: Date
    onSelectDate: (newDate: Date) => void
    locale?: Locale
    data: CalendarData
}

export const WeekCalendar: FC<Props> = ({
    currentMonth,
    currentWeek,
    selectedDate,
    locale,
    onSelectDate,
    data,
}) => {
    const [menuOpen, setMenuOpen] = useState(false)
    const [anchorPosition, setAnchorPosition] = useState({ top: 0, left: 0 })
    const [createEventDate, setCreateEventDate] = useState<Date | undefined>(undefined)

    const handleClose = () => {
        setMenuOpen(false)
    }

    const startDate = startOfWeek(currentMonth, { weekStartsOn: 1 })

    return (
        <Stack
            sx={{
                height: '100%',
                width: '100%',
                p: 0,
            }}>
            <CreateEventMenu
                selectedDate={createEventDate}
                open={menuOpen}
                onClose={handleClose}
                anchorReference="anchorPosition"
                anchorPosition={{ top: anchorPosition.top, left: anchorPosition.left }}
            />
            <Stack
                role="row"
                direction="row"
                sx={{
                    width: '100%',
                    p: 0,
                    borderBottom: '1px solid #F2F2F2',
                }}>
                {daysToRender.map((dayIndex) => {
                    const weekDayFormat = 'eeeeee'
                    const dateFormat = 'd'
                    const day = addDays(startDate, dayIndex)
                    const isCurrentDay = isToday(day)

                    return (
                        <Stack
                            role="columnheader"
                            key={`day-header-${day.toISOString()}`}
                            direction="row"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                height: '100%',
                                width: '100%',
                                maxWidth: 'calc(100% / 7)',
                                gap: 0.5,
                                p: 2,
                            }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    textTransform: 'capitalize',
                                }}>
                                {format(day, weekDayFormat, { locale })}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: '20px',
                                    minWidth: '20px',
                                    borderRadius: '50%',
                                    backgroundColor: isCurrentDay ? '#006CFD' : 'transparent',
                                    color: isCurrentDay ? 'white' : '#1B1C1D',
                                }}>
                                {format(day, dateFormat, { locale })}
                            </Typography>
                        </Stack>
                    )
                })}
            </Stack>
            <Stack
                role="row"
                direction="row"
                sx={{
                    height: '100%',
                    width: '100%',
                    overflowY: 'auto',
                    p: 0,
                    '& > div:not(:last-of-type)': {
                        borderRight: '1px solid #F2F2F2',
                    },
                }}>
                {daysToRender.map((dayIndex) => {
                    const day = addDays(startDate, dayIndex)
                    const isSelected = isSameDay(day, selectedDate)

                    const events = data[day.toISOString()] ?? []

                    return (
                        <Stack
                            role="cell"
                            key={`day-${day.toISOString()}`}
                            direction="column"
                            onClick={() => onSelectDate(day)}
                            onContextMenu={(e) => {
                                e.stopPropagation()
                                e.preventDefault()

                                onSelectDate(day)

                                const canCreateEvent = isFuture(day) || isToday(day)

                                if (canCreateEvent) {
                                    setMenuOpen(true)
                                    setAnchorPosition({ top: e.clientY, left: e.clientX })
                                    setCreateEventDate(day)
                                }
                            }}
                            sx={{
                                height: '100%',
                                width: '100%',
                                maxWidth: 'calc(100% / 7)',
                                p: '4px',
                                gap: '4px',
                                cursor: 'pointer',
                                backgroundColor: isSelected ? '#EBF5FD' : '#FFFFFF',
                                ':hover': {
                                    backgroundColor: isSelected ? '#EBF5FD' : '#F6F6F6',
                                },
                            }}>
                            {events.map((data, index) => (
                                <EventItem
                                    key={`day-${index}-${day.toISOString()}`}
                                    variant="column"
                                    popoverPosition="right"
                                    lineClamp={2}
                                    date={day}
                                    data={data}
                                />
                            ))}
                        </Stack>
                    )
                })}
            </Stack>
        </Stack>
    )
}
